// ContactForm component handles the main contact form UI and logic
import React, { useEffect } from 'react';
import { Link } from '@/components/LocaleLink';
import { cn } from '@/lib/utils';
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useContactForm } from '@/hooks/useContactForm';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { contactFormCopy } from '@/contexts/language/translations/pages/contactForm';
import ContactFormHeader from './contact/ContactFormHeader';
import FormSuccessMessage from './contact/FormSuccessMessage';
import FormFields from './contact/FormFields';
import SubmitButton from './contact/SubmitButton';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '@/config/emailjs';
import { toast } from 'sonner';

interface ContactFormProps {
  service?: string;
  className?: string;
}

const ContactForm = ({ service = "General inquiry", className }: ContactFormProps) => {
  const { language } = useLanguage();
  const c = contactFormCopy[language];
  // Custom hook manages form state, validation, and submission
  const {
    form,
    formRef,
    isSubmitting,
    isSuccess,
    onSubmit,
    resetForm
  } = useContactForm(service);
  
  // Initialize EmailJS on component mount
  useEffect(() => {
    // Initialize EmailJS with the public key (required for sending emails)
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
    // Log for debugging (only shows first 3 chars of key for security)
    console.log("EmailJS initialized with public key:", EMAILJS_CONFIG.PUBLIC_KEY?.substring(0, 3) + '***');
    console.log("Current environment:", window.location.hostname);
  }, []);
  
  // Function to test EmailJS connection (for development/debugging)
  const testEmailConnection = async () => {
    try {
      toast.info("Testing EmailJS connection...");
      // Log the config being used (partial key for security)
      console.log("EmailJS config being used:", {
        serviceId: EMAILJS_CONFIG.SERVICE_ID,
        templateId: EMAILJS_CONFIG.TEMPLATE_ID,
        publicKey: EMAILJS_CONFIG.PUBLIC_KEY?.substring(0, 3) + '***' // Only log first 3 chars for security
      });
      
      // Send a test email using EmailJS
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID, 
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          from_name: "Test User",
          from_email: "test@example.com",
          from_company: "Test Company",
          message: "This is a test message to verify EmailJS is working correctly.",
          to_email: EMAILJS_CONFIG.DEFAULT_TO_EMAIL,
          service_type: "Test"
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      
      console.log("Test email sent successfully:", response);
      toast.success("Test connection successful! Check your email inbox.");
    } catch (error) {
      // Handle and display errors
      console.error("Error testing EmailJS connection:", error);
      let errorMessage = "Failed to connect to EmailJS.";
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      } else if (typeof error === 'object' && error !== null) {
        // Extract more information from EmailJS error response
        const emailJsError = error as any;
        if (emailJsError.text) {
          errorMessage += ` ${emailJsError.text}`;
        }
        if (emailJsError.status) {
          errorMessage += ` (Status: ${emailJsError.status})`;
        }
      }
      toast.error(errorMessage);
    }
  };

  // Use this to force showing the test button in development, regardless of hostname
  // (You can set this to false in production)
  const isDevelopmentMode = true; // Force to true to make sure the button is visible

  return (
    // Main container for the contact form - centered with max width
    <div className={cn("p-6 md:p-8 lg:p-10 rounded-2xl bg-[#FFF4E8] border border-gray-200 shadow-sm max-w-2xl mx-auto", className)}>
      {/* Header with service name and instructions */}
      <ContactFormHeader service={service} />
      
      {/* Show success message if form was submitted successfully, otherwise show the form */}
      {isSuccess ? (
        <FormSuccessMessage service={service} onReset={resetForm} />
      ) : (
        <Form {...form}>
          <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Main form fields: name, email, company, employees, message, human check */}
            <FormFields form={form} />

            {/* Privacy notice — submitting the form is the acknowledgement, no checkbox */}
            <p className="text-sm text-muted-foreground">
              {c.privacyNotice}
              <Link to="/privacy" className="text-foreground underline hover:text-foreground/80">
                {c.privacyPolicyLink}
              </Link>
              {c.privacyNoticeEnd}
            </p>

            {/* Submit button with loading state */}
            <SubmitButton isSubmitting={isSubmitting} />
          </form>
        </Form>
      )}
    </div>
  );
};

export default ContactForm;
